<template>
  <div id="app" :style="cssVars">
    <SystemNotification/>
    <Loader :show="showLoader"/>
    <div class="full-expand">
      <MaintenanceBanner v-if="!hideMaintenanceBanner"></MaintenanceBanner>
      <BrowserBanner v-if="showBrowserBanner"></BrowserBanner>
      <AppContainer
        v-if="isAdminView == false && !isNotFoundView"
        :title="conferenceCallDetails.CallTitle || conferenceCallDetails.PresTitle"
        :conferenceStartDate="conferenceCallStartTime"
        :conferenceDuration="conferenceCallDuration"
        :topic="topic"
        :supportNumber= "supportNumber"
      >
        <router-view></router-view>
      </AppContainer>
      <AdminAppContainer v-if="isAdminView == true">
        <router-view></router-view>
      </AdminAppContainer>
      <NotFoundContainer v-if="isNotFoundView">
      </NotFoundContainer>
      <SiteNotAvailable v-if="isSiteNotAvailable">
      </SiteNotAvailable>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import moment from "moment";
import AdminAppContainer from "@/components/layout/AdminAppContainer";
import NotFoundContainer from "@/components/layout/NotFoundContainer";
import SiteNotAvailable from "@/components/layout/SiteNotAvailableContainer";
import SystemNotification from "@/components/common/SystemNotification";
import MaintenanceBanner from "@/components/common/MaintenanceBanner";
import AppContainer from "@/components/layout/AppContainer";
import DateFilters from "@/shared/customDateFilter";
import Loader from "@/components/ui/Loader";
import Constants from "@/utils/constants";
import { getEmailAddressFromSession, isIE, lightenDarkenColor } from '@/utils/helper';
import BrowserBanner from '@/components/common/BrowserBanner';

// Composables
import { 
  useConferenceCall, 
  useService, 
  useGlobal, 
  useCorporateBranding, 
  useLogin 
} from '@/composables';

// Setup composables
const { 
  conferenceCallDetails, 
  utcTimeString, 
  isSGMType,
  fetchConferenceCallDetails 
} = useConferenceCall();

const { 
  pendingServices, 
  isLoading 
} = useService();

const { 
  isAdminView, 
  isNotFoundView, 
  hideMaintenanceBanner, 
  isSiteNotAvailable, 
  hideBrowserBanner, 
  isMeetingEnded,
  setIsIeOrEdge 
} = useGlobal();

const { 
  hasEventBranding 
} = useCorporateBranding();

const { 
  setEmailAddress 
} = useLogin();

// Reactive data
const theme = Constants.Theme;
const cssVars = ref({});

// Computed properties
const showLoader = computed(() => {
  return !!pendingServices.value.length || isLoading.value;
});

const conferenceCallStartTime = computed(() => {
  if(isSGMType.value){
    return utcTimeString.value;
  }
  let dateTimeString = DateFilters.userLocalDateTimeFormat(
    conferenceCallDetails.value.StartTimeUtc,
    Constants.RegUserLocaleDateTimeFormat,
  );
  return dateTimeString.concat(` ${conferenceCallDetails.value.StartTimeAcronym}`);
});

const conferenceCallDuration = computed(() => {
  return DateFilters.getDurationAsMinute(
    conferenceCallDetails.value.StartTimeUtc,
    conferenceCallDetails.value.EndTimeUtc,
  );
});

const topic = computed(() => {
  return conferenceCallDetails.value.Topic;
});

const supportNumber = computed(() => {
  if(conferenceCallDetails.value.ClientProfile && conferenceCallDetails.value.ClientProfile.BridgeLocationsMap) {
    var primaryBridgeLocation = conferenceCallDetails.value.ClientProfile.BridgeLocationsMap.find(x => x.IsPrimary);
    return primaryBridgeLocation ? primaryBridgeLocation.SupportNumber : "";   
  }
  return "";
});

const showBrowserBanner = computed(() => {
  if(conferenceCallDetails.value.ShowType === "Live Video + Slides"){
    const isRegistrationFlow = window.location.pathname.indexOf('/login') !== -1 || 
                              window.location.pathname.indexOf('/register') !== -1  || 
                              window.location.pathname.indexOf('/supplemental-register') !== -1 || 
                              window.location.pathname.indexOf('/disclaimer') !== -1 || 
                              window.location.pathname.indexOf('/validation') !== -1 || 
                              window.location.pathname.indexOf('/email-confirmation')!==-1;
    const isBrowserChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isBrowserChromiumEdge = isBrowserChrome && navigator.userAgent.indexOf('Edg/') > -1;
    const isBrowserIosChrome = navigator.userAgent.indexOf('CriOS') > -1;
    const isBrowserFirefox = /firefox/.test(navigator.userAgent.toLowerCase());

    return !hideBrowserBanner.value && isRegistrationFlow && !(isBrowserChrome || isBrowserChromiumEdge || isBrowserIosChrome || isBrowserFirefox);
  }
  else {
    return !hideBrowserBanner.value && (/Trident\/|MSIE/.test(window.navigator.userAgent));
  }
});

// Methods
const createBrandingColors = () => {
  const docStyle = getComputedStyle(document.documentElement);
  const myVarVal = docStyle.getPropertyValue('--primary-color');
  // Use negative value for darker shade. i.e. 0.15 = 15% darker
  // Use positive value for lighter shade
  cssVars.value = {
    '--primary-color-dark': lightenDarkenColor(myVarVal.trim(), -0.15),
    '--primary-color-darker': lightenDarkenColor(myVarVal.trim(), -0.3),
  };
};

setIsIeOrEdge(isIE());
const emailAddress = getEmailAddressFromSession();
if (emailAddress) {
  setEmailAddress(emailAddress);
}

// Lifecycle hooks
onMounted(() => {
  createBrandingColors();
});

// Watchers
watch(hasEventBranding, async (newVal) => {
  if (newVal) {
    createBrandingColors();
  }
}, { deep: true });


</script>